import { CalendarSurfaceItemContentProps } from './CalendarSurfaceItem.types';
import calculateFieldSizing from '../../utils/calculateFieldSizings';
import calculateIconLocationSizings, { IconLocationType } from '../../utils/calculateIconLocation';
import getInnerHeight from '../../utils/getInnerHeight';
import CalendarSurfaceItemClipping from '../CalendarSurfaceItemClipping';
import { renderCharmWrapper, renderIconWrapper } from '../CalendarSurfaceItemComponentRenderer';
import { observer } from 'mobx-react';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import loc from 'owa-localize';
import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import * as React from 'react';

const classNames = require('classnames');
const surfaceItemStyles = require('../CalendarSurfaceItem.scss');

/**
 * State for the CalendarSurfaceItem60
 */
export interface CalendarSurfaceItem60State {
    lineHeights: number[];
    subjectLines: number;
    iconLocation: IconLocationType;
}

/**
 * The Component for a Calendar Surface item that's exactly 60 minutes long
 */
@observer
export default class CalendarSurfaceItem60 extends React.Component<
    CalendarSurfaceItemContentProps,
    CalendarSurfaceItem60State
> {
    wrapperRef: HTMLElement;
    subjectRef: HTMLElement;
    asyncId: number;

    constructor(props: CalendarSurfaceItemContentProps) {
        super(props);
        this.state = {
            lineHeights: [0, 0, 0],
            subjectLines: 0,
            iconLocation: IconLocationType.Unknown,
        };
    }

    render() {
        const { item, colorScheme, charmId, allowEmptySubject } = this.props;
        const { lineHeights, subjectLines, iconLocation } = this.state;

        let subject = item.Subject;
        if (!subject && !allowEmptySubject) {
            subject = loc(noSubject);
        }
        const location = item.Location && item.Location.DisplayName;
        const organizer = item.Organizer && item.Organizer.Mailbox.Name;
        return (
            <div className={surfaceItemStyles.itemContent} ref={this.setWrapperRef}>
                <h2 className={classNames(surfaceItemStyles.itemSubject)} ref={this.setSubjectRef}>
                    <CalendarSurfaceItemClipping lineHeight={lineHeights[0]} lines={subjectLines}>
                        {renderCharmWrapper(item, colorScheme, charmId)}
                        <span>{subject ? subject : location}</span>
                    </CalendarSurfaceItemClipping>
                </h2>
                {subject && location && (
                    <div
                        className={classNames(
                            surfaceItemStyles.locationWrapper,
                            surfaceItemStyles.itemElementContent
                        )}>
                        <CalendarSurfaceItemClipping lineHeight={lineHeights[1]} lines={1}>
                            {location}
                        </CalendarSurfaceItemClipping>
                        {iconLocation == IconLocationType.Location &&
                            renderIconWrapper(item, false)}
                    </div>
                )}
                {organizer && item.IsMeeting && (
                    <div className={classNames(surfaceItemStyles.itemElementContent)}>
                        <CalendarSurfaceItemClipping lineHeight={lineHeights[2]} lines={1}>
                            {organizer}
                        </CalendarSurfaceItemClipping>
                        {iconLocation == IconLocationType.Organizer &&
                            renderIconWrapper(item, false)}
                    </div>
                )}
                {iconLocation == IconLocationType.BelowAll && renderIconWrapper(item, false)}
            </div>
        );
    }

    public componentDidMount() {
        this.asyncId = requestAnimationFrame(() => this.resetFieldSizing());
    }

    public componentWillUnmount() {
        if (this.asyncId) {
            cancelAnimationFrame(this.asyncId);
        }
    }

    private setWrapperRef = (wrapperRef: HTMLElement) => {
        this.wrapperRef = wrapperRef;
    };

    private setSubjectRef = (subjectRef: HTMLElement) => {
        this.subjectRef = subjectRef;
    };

    private resetFieldSizing() {
        const lineHeights = [
            SurfaceConstants.subjectLineHeight,
            SurfaceConstants.detailsLineHeight,
            SurfaceConstants.detailsLineHeight,
        ];
        const totalHeight = getInnerHeight(this.wrapperRef);
        const subjectHeight = getInnerHeight(this.subjectRef);
        const locationHeight = this.props.item.Location ? SurfaceConstants.detailsLineHeight : 0;
        const organizerHeight =
            this.props.item.Organizer && this.props.item.IsMeeting
                ? SurfaceConstants.detailsLineHeight
                : 0;

        const lines = calculateFieldSizing(lineHeights, totalHeight, [
            subjectHeight,
            locationHeight,
            organizerHeight,
        ]);

        // making room for the iconwrapper:
        // if the subject is going down to the bottom, it'll overlap- remove bottom line of subject
        if (subjectHeight >= totalHeight - lineHeights[0] && lines[1] == 0) {
            lines[0] = lines[0] - 1;
        }

        const iconLocation = calculateIconLocationSizings(
            lineHeights,
            lines,
            SurfaceConstants.detailsLineHeight,
            totalHeight
        );

        // https://reactjs.org/docs/react-component.html
        // Calling setState() in this method will trigger an extra rendering, but it will happen before the browser updates the screen.
        // This guarantees that even though the render() will be called twice in this case, the user won’t see the intermediate state.
        // Use this pattern with caution because it often causes performance issues.
        // It can, however, be necessary for cases like modals and tooltips when you need to measure a DOM node before rendering something
        // that depends on its size or position.
        this.setState({
            lineHeights: lineHeights,
            subjectLines: lines[0],
            iconLocation: iconLocation,
        });
    }
}
